import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Users,
  CheckCircle2,
  Clock,
  XCircle,
  Search,
  RefreshCw,
} from "lucide-react";

const STATUS_META = {
  pending_payment: {
    label: "Pending",
    icon: Clock,
    cls: "text-amber-600 bg-amber-50 border-amber-200",
  },
  active: {
    label: "Active",
    icon: CheckCircle2,
    cls: "text-emerald-700 bg-emerald-50 border-emerald-200",
  },
  failed: {
    label: "Failed",
    icon: XCircle,
    cls: "text-red-700 bg-red-50 border-red-200",
  },
  cancelled: {
    label: "Cancelled",
    icon: XCircle,
    cls: "text-gray-600 bg-gray-50 border-gray-200",
  },
};

export default function AdminSubscriptions() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, failed: 0 });
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/subscriptions");
      setRows(data.subscriptions || []);
      setStats(data.stats || { total: 0, active: 0, pending: 0, failed: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);  // eslint-disable-line

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (
      (r.full_name || "").toLowerCase().includes(s) ||
      (r.email || "").toLowerCase().includes(s) ||
      (r.firm_name || "").toLowerCase().includes(s) ||
      (r.arn_number || "").toLowerCase().includes(s)
    );
  });

  return (
    <div data-testid="admin-subscriptions" className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-gray-900">
            Subscriptions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Track MFD owner subscriptions, activations, and payment states.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="admin-subscriptions-search"
              placeholder="Search name, email, firm, ARN"
              className="pl-9 pr-4 py-2 text-sm rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#1a3f3a] w-72"
            />
          </div>
          <button
            onClick={load}
            data-testid="admin-subscriptions-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile label="Total" value={stats.total} icon={Users} />
        <StatTile label="Active" value={stats.active} icon={CheckCircle2} tone="emerald" />
        <StatTile label="Pending" value={stats.pending} icon={Clock} tone="amber" />
        <StatTile label="Failed" value={stats.failed} icon={XCircle} tone="red" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-subscriptions-table">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-5 py-3">Name & Firm</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">ARN</th>
                <th className="text-left px-5 py-3">AUM / Clients</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-gray-400">
                    No subscriptions yet.
                  </td>
                </tr>
              )}
              {filtered.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.pending_payment;
                const Icon = meta.icon;
                return (
                  <tr key={r.subscription_id} className="hover:bg-gray-50/60">
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-900">{r.full_name}</div>
                      <div className="text-xs text-gray-500">{r.firm_name}</div>
                    </td>
                    <td className="px-5 py-4 text-gray-700">{r.email}</td>
                    <td className="px-5 py-4 text-gray-700">{r.arn_number}</td>
                    <td className="px-5 py-4 text-gray-700">
                      <div>{r.approx_aum || "—"}</div>
                      <div className="text-xs text-gray-500">
                        {r.num_clients || 0} clients
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${meta.cls}`}
                      >
                        <Icon size={12} /> {meta.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-600 text-xs">
                      {r.created_at
                        ? new Date(r.created_at).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, tone }) {
  const toneCls =
    tone === "emerald"
      ? "text-emerald-700 bg-emerald-50"
      : tone === "amber"
      ? "text-amber-700 bg-amber-50"
      : tone === "red"
      ? "text-red-700 bg-red-50"
      : "text-gray-700 bg-gray-100";
  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-5"
      data-testid={`admin-subscription-stat-${label.toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-gray-500">
          {label}
        </span>
        <span className={`h-7 w-7 rounded-md grid place-items-center ${toneCls}`}>
          <Icon size={14} />
        </span>
      </div>
      <div className="mt-3 text-3xl font-light text-gray-900 tabular-nums">
        {value || 0}
      </div>
    </div>
  );
}
